import React, { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ChevronDown, MoreVertical,
  HelpCircle, Edit, Trash2, Eye, X,
  Plus, PlusCircle, Settings, Layout,
  ThumbsUp, Globe, FolderPlus, MessageSquare,
  AlertCircle, Check, FileText, Users, ArrowUpRight,
  Bell, Camera, Ban, ExternalLink, Lock, Unlock,
  Facebook, Twitter, Instagram, Youtube, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import SocialMediaManagement from "../CONTENT EDITOR/SocialMediaManagement";
import { SocialLinkTypeEnum } from "../../modules/shared/model/contact-info.interfaces";
import FaqCard from "../../components/faq/FaqCard";
import FaqModal from "../../components/faq/FaqModal";
import LanguageDropdown from "../../components/ui/LanguageDropdown";
import CountryDropdown from "../../components/ui/CountryDropdown";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchCommonQuestions,
  createCommonQuestion,
  updateCommonQuestion,
  deleteCommonQuestion,
  bulkCreateCommonQuestions,
  bulkUpdateCommonQuestions,
  setSearchQuery,
  setSelectedCategory,
  setSelectedLanguage,
  setSelectedCountry,
  clearFilters
} from "../../redux/Slices/CommonQuestionsSlice";
import { getAllLanguages } from "../../redux/Actions/LanguageAction";
import { CommonQuestion, CommonQuestionCreateDto, CommonQuestionUpdateDto } from "../../modules/shared/model/common-questions.interfaces";
import { CommonQuestionCategoryEnum } from "../../modules/shared/model/common-questions.enum";
import { UserPermissions } from "../../components/UserPermissions";

// Note: lucide-react does not have a TikTok icon by default.
// We"ll use a placeholder (Globe) and note this for potential custom implementation.
const Tiktok = Globe; // Placeholder; replace with custom TikTok icon if available.

const HelpCenter = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const {
    commonQuestions,
    loading,
    error,
    totalCount,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    searchQuery,
    selectedCategory,
    selectedLanguage,
    selectedCountry,
    categoryCounts
  } = useAppSelector((state) => state.commonQuestions);

  // Local state
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [showFaqDetails, setShowFaqDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("faqs");
  const [editingFaq, setEditingFaq] = useState<CommonQuestion | null>(null);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Calculate effective current page early
  const effectiveCurrentPage = localCurrentPage || currentPage;

  // Load initial data
  useEffect(() => {
    dispatch(fetchCommonQuestions({
      page: 1,
      limit: itemsPerPage,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
    dispatch(getAllLanguages({
      page: 1,
      limit: 10,
      isShowLoader: false
    }));
  }, [dispatch, itemsPerPage]);

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setSearchQuery(query));
    dispatch(fetchCommonQuestions({
      page: 1,
      limit: itemsPerPage,
      search: query,
      category: selectedCategory,
      language: selectedLanguage,
      country: selectedCountry,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
  };

  // Handle category filter
  const handleCategoryChange = (category: string) => {
    const categoryId = category === "all" ? null : parseInt(category);
    dispatch(setSelectedCategory(categoryId));
    dispatch(fetchCommonQuestions({
      page: 1,
      limit: itemsPerPage,
      search: searchQuery,
      category: categoryId,
      language: selectedLanguage,
      country: selectedCountry,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
  };

  // Handle language filter
  const handleLanguageChange = (language: string) => {
    const languageId = language === "all" ? null : language;
    dispatch(setSelectedLanguage(languageId));
    dispatch(fetchCommonQuestions({
      page: 1,
      limit: itemsPerPage,
      search: searchQuery,
      category: selectedCategory,
      language: languageId,
      country: selectedCountry,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
  };

  // Handle country filter
  const handleCountryChange = (country: {id: string; name: string; code: string} | null) => {
    const countryId = country ? country.id : null;
    dispatch(setSelectedCountry(countryId));
    dispatch(fetchCommonQuestions({
      page: 1,
      limit: itemsPerPage,
      search: searchQuery,
      category: selectedCategory,
      language: selectedLanguage,
      country: countryId,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
  };

  // Handle FAQ operations
  const handleCreateFaq = useCallback(async (data: CommonQuestionCreateDto | { commonQuestions: CommonQuestionCreateDto[] }) => {
    try {
      // Check if it's bulk data
      if ("commonQuestions" in data) {
        await dispatch(bulkCreateCommonQuestions(data)).unwrap();
      } else {
        await dispatch(createCommonQuestion(data)).unwrap();
      }
      setShowAddModal(false);
      // Refresh the list
      dispatch(fetchCommonQuestions({
        page: effectiveCurrentPage,
        limit: itemsPerPage,
        search: searchQuery,
        category: selectedCategory,
        language: selectedLanguage,
        country: selectedCountry,
        sortBy: "createdAt",
        sortOrder: "desc"
      }));
    } catch (error) {
      console.error("Failed to create FAQ:", error);
    }
  }, [dispatch, effectiveCurrentPage, itemsPerPage, searchQuery, selectedCategory, selectedLanguage, selectedCountry]);

  const handleUpdateFaq = useCallback(async (data: CommonQuestionUpdateDto) => {
    if (!editingFaq) return;

    try {
      await dispatch(updateCommonQuestion({ id: editingFaq.id, data })).unwrap();
      setEditingFaq(null);
      // Refresh the list
      dispatch(fetchCommonQuestions({
        page: effectiveCurrentPage,
        limit: itemsPerPage,
        search: searchQuery,
        category: selectedCategory,
        language: selectedLanguage,
        country: selectedCountry,
        sortBy: "createdAt",
        sortOrder: "desc"
      }));
    } catch (error) {
      console.error("Failed to update FAQ:", error);
    }
  }, [dispatch, editingFaq, effectiveCurrentPage, itemsPerPage, searchQuery, selectedCategory, selectedLanguage, selectedCountry]);

  const handleDeleteFaq = useCallback(async (faq: CommonQuestion) => {
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      try {
        await dispatch(deleteCommonQuestion(faq.id)).unwrap();
        // Refresh the list
        dispatch(fetchCommonQuestions({
          page: effectiveCurrentPage,
          limit: itemsPerPage,
          search: searchQuery,
          category: selectedCategory,
          language: selectedLanguage,
          country: selectedCountry,
          sortBy: "createdAt",
          sortOrder: "desc"
        }));
      } catch (error) {
        console.error("Failed to delete FAQ:", error);
      }
    }
  }, [dispatch, effectiveCurrentPage, itemsPerPage, searchQuery, selectedCategory, selectedLanguage, selectedCountry]);

  const handleEditFaq = useCallback((faq: CommonQuestion) => {
    setEditingFaq(faq);
  }, []);

  // Dynamic categories based on enum and API counts
  const categories = useMemo(() => {
    const categoryIcons = {
      1: HelpCircle,
      2: Users,
      3: FileText,
      4: MessageSquare
    };

    return Object.entries(CommonQuestionCategoryEnum)
      .filter(([key, value]) => typeof value === "number")
      .map(([key, value]) => ({
        id: value,
        name: key,
        icon: categoryIcons[value as keyof typeof categoryIcons] || HelpCircle,
        faqs: categoryCounts[value] || 0,
        lastUpdated: new Date().toISOString().split("T")[0]
      }));
  }, [categoryCounts]);



  // Notification Modal
  const NotificationModal = ({ isOpen, onClose, user }) => {
    const [notificationType, setNotificationType] = useState("banner");
    const [notificationTitle, setNotificationTitle] = useState("");
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationPriority, setNotificationPriority] = useState("normal");
    const [notificationAction, setNotificationAction] = useState("");
    const [notificationExpiry, setNotificationExpiry] = useState("");

    if (!isOpen || !user) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4"
        >
          <div className="p-4 md:p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-semibold truncate">Send Notification to {user.name}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0 ml-2"
              >
                <X className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
          </div>

            <div className="p-4 md:p-6 space-y-4" style={{ textAlign: "left" }}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notification Type</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                >
                  <option value="banner">Banner</option>
                  <option value="popup">Popup</option>
                  <option value="message">In-app Message</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  placeholder="Enter notification title"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  rows={4}
                  placeholder="Enter notification message"
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  value={notificationPriority}
                  onChange={(e) => setNotificationPriority(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action URL (Optional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  placeholder="Enter URL for notification action"
                  value={notificationAction}
                  onChange={(e) => setNotificationAction(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry (Optional)</label>
                <input
                  type="datetime-local"
                  className="w-full px-3 py-2 border rounded-lg text-sm md:text-base"
                  value={notificationExpiry}
                  onChange={(e) => setNotificationExpiry(e.target.value)}
                />
              </div>

              <div className="pt-4 border-t flex flex-col sm:flex-row justify-end gap-3">
                <Button variant="outline" onClick={onClose} className="w-full sm:w-auto text-sm md:text-base">
                  Cancel
                </Button>
                <Button className="w-full sm:w-auto text-sm md:text-base">
                  Send Notification
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Permission Modal
  const PermissionModal = ({ isOpen, onClose, user }) => {
    const [cameraAccess, setCameraAccess] = useState(user?.permissions.camera || false);
    const [notificationAccess, setNotificationAccess] = useState(user?.permissions.notifications || false);

    if (!isOpen || !user) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4"
        >
          <div className="p-4 md:p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-semibold truncate">Manage Permissions for {user.name}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0 ml-2"
              >
                <X className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
          </div>

            <div className="p-4 md:p-6 space-y-4" style={{ textAlign: "left" }}>
              <div className="bg-blue-50 p-3 md:p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs md:text-sm text-blue-700">
                    Changing permissions will affect this user&apos;s ability to interact with certain features of the platform.
                  </p>
                </div>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Camera className="h-4 w-4 md:h-5 md:w-5 text-gray-700 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm md:text-base">Camera Access</p>
                      <p className="text-xs md:text-sm text-gray-500">Allow the user to use video call features</p>
                    </div>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-2">
                    <input
                      type="checkbox"
                      value=""
                      className="sr-only peer"
                      checked={cameraAccess}
                      onChange={() => setCameraAccess(!cameraAccess)}
                    />
                    <div className="w-10 h-5 md:w-11 md:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 md:after:h-5 md:after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Bell className="h-4 w-4 md:h-5 md:w-5 text-gray-700 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm md:text-base">Notification Access</p>
                      <p className="text-xs md:text-sm text-gray-500">Allow the user to receive notifications</p>
                    </div>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-2">
                    <input
                      type="checkbox"
                      value=""
                      className="sr-only peer"
                      checked={notificationAccess}
                      onChange={() => setNotificationAccess(!notificationAccess)}
                    />
                    <div className="w-10 h-5 md:w-11 md:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 md:after:h-5 md:after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t flex flex-col sm:flex-row justify-end gap-3">
                <Button variant="outline" onClick={onClose} className="w-full sm:w-auto text-sm md:text-base">
                  Cancel
                </Button>
                <Button className="w-full sm:w-auto text-sm md:text-base">
                  Save Changes
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };


  // User Card Component
  const UserCard = ({ user }) => (
    <Card className="hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
      <div className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <h3 className="font-semibold text-sm md:text-base truncate">{user.name}</h3>
              <Badge variant={undefined} className={`${user.type === "professional" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"} text-xs w-fit`}>
                {user.type.charAt(0).toUpperCase() + user.type.slice(1)}
              </Badge>
            </div>
            <p className="text-xs md:text-sm text-gray-600 mt-1 truncate">{user.email}</p>
            {user.category && <p className="text-xs md:text-sm text-gray-600 truncate">{user.category}</p>}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2 text-xs md:text-sm"
              onClick={() => {
                setSelectedUser(user);
                setShowNotificationModal(true);
              }}
            >
              <Bell className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden xs:inline">Notify</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2 text-xs md:text-sm"
              onClick={() => {
                setSelectedUser(user);
                setShowPermissionModal(true);
              }}
            >
              <Settings className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden xs:inline">Permissions</span>
            </Button>
          </div>
        </div>

        <div className="mt-4 md:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Camera className="h-3 w-3 md:h-4 md:w-4 text-gray-500 flex-shrink-0" />
                <span className="text-xs md:text-sm truncate">Camera Access</span>
              </div>
              <Badge variant={undefined} className={`${user.permissions.camera ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} text-xs flex-shrink-0 ml-2`}>
                {user.permissions.camera ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Bell className="h-3 w-3 md:h-4 md:w-4 text-gray-500 flex-shrink-0" />
                <span className="text-xs md:text-sm truncate">Notifications</span>
              </div>
              <Badge variant={undefined} className={`${user.permissions.notifications ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} text-xs flex-shrink-0 ml-2`}>
                {user.permissions.notifications ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  // Pagination logic
  const indexOfLastItem = effectiveCurrentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  
  // Calculate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      const startPage = Math.max(1, effectiveCurrentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust start page if we're near the end
      const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1);
      
      for (let i = adjustedStartPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const handlePageChange = useCallback((newPage: number) => {
    setLocalCurrentPage(newPage);
    dispatch(fetchCommonQuestions({
      page: newPage,
      limit: itemsPerPage,
      search: searchQuery,
      category: selectedCategory,
      language: selectedLanguage,
      country: selectedCountry,
      sortBy: "createdAt",
      sortOrder: "desc"
    }));
  }, [dispatch, itemsPerPage, searchQuery, selectedCategory, selectedLanguage, selectedCountry]);

  // Social Media Section using SocialMediaManagement component
  const SocialMediaSection = () => (
    <SocialMediaManagement />
  );

  // Main render section
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Help Center</h1>
                <p className="text-gray-600 mt-1 text-sm md:text-base">Manage FAQs, social media, and user permissions</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                  onClick={() => setShowAddModal(true)}
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden xs:inline">Add FAQ</span>
                  <span className="xs:hidden">Add</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <FolderPlus className="h-4 w-4" />
                  <span className="hidden xs:inline">Add Category</span>
                  <span className="xs:hidden">Category</span>
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-gray-500 truncate">Total FAQs</p>
                    <p className="text-xl md:text-2xl font-semibold">{totalCount}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 ml-2">
                    <HelpCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-gray-500 truncate">Categories</p>
                    <p className="text-xl md:text-2xl font-semibold">{categories.length}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 ml-2">
                    <Layout className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-gray-500 truncate">Total Views</p>
                    <p className="text-xl md:text-2xl font-semibold">2.5k</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 ml-2">
                    <Eye className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-gray-500 truncate">Helpful Rate</p>
                    <p className="text-xl md:text-2xl font-semibold">92%</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0 ml-2">
                    <ThumbsUp className="h-4 w-4 md:h-5 md:w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Content Tabs */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="flex overflow-x-auto scrollbar-hide">
                <button
                  className={`px-3 md:px-4 py-3 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === "faqs" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-900"}`}
                  onClick={() => setActiveTab("faqs")}
                >
                  FAQs
                </button>
                <button
                  className={`px-3 md:px-4 py-3 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === "social" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-900"}`}
                  onClick={() => setActiveTab("social")}
                >
                  <span className="hidden sm:inline">Social Media</span>
                  <span className="sm:hidden">Social</span>
                </button>
                <button
                  className={`px-3 md:px-4 py-3 font-medium text-sm whitespace-nowrap flex-shrink-0 ${activeTab === "users" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-900"}`}
                  onClick={() => setActiveTab("users")}
                >
                  <span className="hidden sm:inline">User Permissions</span>
                  <span className="sm:hidden">Users</span>
                </button>
              </div>
            </div>

            {/* Search and Filters - Only show for FAQs */}
            {activeTab === "faqs" && (
              <Card className="p-4">
                <div className="space-y-4">
                  {/* Search and Filter Button */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search FAQs..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm md:text-base"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2"
                    >
                      <Filter className="h-4 w-4" />
                      <span className="hidden xs:inline">Filters</span>
                    </Button>
                  </div>

                  {/* Filter Controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <select
                      value={selectedCategory || "all"}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm md:text-base w-full"
                    >
                      <option value="all">All Categories</option>
                      {Object.entries(CommonQuestionCategoryEnum)
                        .filter(([key, value]) => typeof value === "number")
                        .map(([key, value]) => (
                          <option key={value} value={value}>{key}</option>
                        ))}
                    </select>
                    <div className="w-full">
                      <LanguageDropdown
                        selectedLanguage={selectedLanguage ? { id: selectedLanguage, name: selectedLanguage, initial: selectedLanguage, direction: { id: "1", name: "LTR" }, isDefault: false, isTranslationEnable: false, isAutomaticSync: false, isNotify: false, autoTranslate: { id: "1" }, translationMemory: { id: "1" }, categories: [] } : null}
                        onLanguageSelect={(lang) => handleLanguageChange(lang?.id || "all")}
                        placeholder="All Languages"
                        className="w-full"
                      />
                    </div>
                    <div className="w-full">
                      <CountryDropdown
                        selectedCountry={selectedCountry ? { id: selectedCountry, name: selectedCountry, code: selectedCountry } : null}
                        onCountrySelect={handleCountryChange}
                        placeholder="All Countries"
                        className="w-full"
                      />
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm md:text-base w-full"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="views">Most Viewed</option>
                      <option value="helpful">Most Helpful</option>
                    </select>
                  </div>
                </div>
              </Card>
            )}

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === "faqs" && (
                <>
                  {/* Categories Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
                    {categories.map(category => (
                      <Card key={category.id} className="hover:shadow-md transition-shadow">
                        <div className="p-4 md:p-6 flex flex-col h-full">
                          <div className="p-2 bg-blue-50 rounded-lg w-fit">
                            <category.icon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                          </div>
                          <h3 className="font-medium mt-3 md:mt-4 text-sm md:text-base truncate">{category.name}</h3>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">{category.faqs} FAQs</p>
                          <p className="text-xs text-gray-400 mt-auto pt-3 md:pt-4">
                            Updated {new Date(category.lastUpdated).toLocaleDateString()}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* FAQs Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {loading && commonQuestions.length === 0 ? (
                      <div className="col-span-1 md:col-span-2 flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 md:h-8 md:w-8 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-500 text-sm md:text-base">Loading FAQs...</span>
                      </div>
                    ) : commonQuestions.length === 0 ? (
                      <div className="col-span-1 md:col-span-2 flex flex-col items-center justify-center py-12 text-gray-500">
                        <HelpCircle className="h-8 w-8 md:h-12 md:w-12 text-gray-300 mb-4" />
                        <p className="text-base md:text-lg font-medium">No FAQs found</p>
                        <p className="text-xs md:text-sm text-gray-400">Create your first FAQ to get started</p>
                      </div>
                    ) : (
                      commonQuestions.map(faq => (
                        <FaqCard
                          key={faq.id}
                          faq={faq}
                          onEdit={handleEditFaq}
                          onDelete={handleDeleteFaq}
                        />
                      ))
                    )}
                  </div>

                  {/* Pagination */}
                  <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mt-6 mb-8">
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 order-2 lg:order-1">
                      <p className="text-xs md:text-sm text-gray-500 text-center sm:text-left">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalCount)} of {totalCount} FAQs
                      </p>
                      {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs md:text-sm text-gray-500 hidden sm:inline">Page</span>
                          <select
                            value={effectiveCurrentPage}
                            onChange={(e) => handlePageChange(parseInt(e.target.value))}
                            className="px-2 py-1 border rounded text-xs md:text-sm"
                            disabled={loading}
                          >
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <option key={page} value={page}>
                                {page} of {totalPages}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1 order-1 lg:order-2 overflow-x-auto">
                        {/* First Page */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          disabled={effectiveCurrentPage === 1 || loading}
                          className="px-2 py-1 text-xs md:text-sm flex-shrink-0"
                        >
                          <span className="hidden sm:inline">««</span>
                          <span className="sm:hidden">First</span>
                        </Button>
                        
                        {/* Previous Page */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(effectiveCurrentPage - 1)}
                          disabled={effectiveCurrentPage === 1 || loading}
                          className="px-2 py-1 text-xs md:text-sm flex-shrink-0"
                        >
                          <span className="hidden sm:inline">«</span>
                          <span className="sm:hidden">Prev</span>
                        </Button>
                        
                        {/* Page Numbers */}
                        <div className="flex items-center gap-1 overflow-x-auto">
                          {getPageNumbers().map((pageNum) => {
                            const isActive = pageNum === effectiveCurrentPage;
                            return (
                              <button
                                key={pageNum}
                                type="button"
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                className={`
                                  px-2 md:px-3 py-1 md:py-2 min-w-[28px] md:min-w-[32px] text-xs md:text-sm font-medium rounded-md border transition-colors flex-shrink-0
                                  ${isActive 
                                    ? "bg-blue-600 text-white border-blue-600 shadow-md font-semibold" 
                                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                  }
                                  ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                                `}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Next Page */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(effectiveCurrentPage + 1)}
                          disabled={!hasNextPage || loading}
                          className="px-2 py-1 text-xs md:text-sm flex-shrink-0"
                        >
                          <span className="hidden sm:inline">»</span>
                          <span className="sm:hidden">Next</span>
                        </Button>
                        
                        {/* Last Page */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          disabled={effectiveCurrentPage === totalPages || loading}
                          className="px-2 py-1 text-xs md:text-sm flex-shrink-0"
                        >
                          <span className="hidden sm:inline">»»</span>
                          <span className="sm:hidden">Last</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === "social" && <SocialMediaSection />}

              {activeTab === "users" && <UserPermissions
                showHeader={true}
                showActions={true}
                showFilters={true}
              />}
            </div>
          </div>
        </div>
      </main>

      {/* Add/Edit FAQ Modal */}
      <FaqModal
        faq={editingFaq}
        isOpen={showAddModal || editingFaq !== null}
        onClose={() => {
          setShowAddModal(false);
          setEditingFaq(null);
        }}
        onSubmit={editingFaq ? handleUpdateFaq : handleCreateFaq}
        loading={loading}
      />


      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        user={selectedUser}
      />

      {/* Permission Modal */}
      <PermissionModal
        isOpen={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        user={selectedUser}
      />

      <SharedFooter3 />
    </div>
  );
};

export default HelpCenter;